// Sign-in / sign-up / practice CTAs on the candidate pages belong to the
// candidate product, not to the recruiter sign-up. The destination below is
// the candidate login page, which carries its own "Create account" switch, so a
// single URL serves both sign in and sign up. There is no /signup route there.
//
// AIIA is a B2B site, but its candidate pages draw most of the site's search
// traffic (interview warmup, AI interview practice, how to pass an AI
// interview). Those visitors are sent to Just One Interview. The scope is the
// candidate feature pages, the candidate hub, the candidate-facing blog posts,
// and the general-interest AI posts. Every other marketing route keeps its recruiter destination, and
// invited-candidate and employer flows are untouched.
import { useEffect, useState } from "react";

import { parseNativeCandidatePath } from "./native/candidatePages.js";

export const CANDIDATE_PRACTICE_URL =
  "https://app.justoneinterview.com/login?utm_source=aiia&utm_medium=referral&utm_campaign=candidate_practice";

/**
 * The practice link tagged with the AIIA page it sits on (`utm_content`), so
 * Just One Interview can report which page sent each sign-up. The links open
 * with `noreferrer`, so the URL is the only thing that crosses over. The value
 * must satisfy the portal's `[a-z0-9_.-]{1,64}` check or it is dropped there.
 * `lang` (a JOI language code) opens the app in that language: the native
 * pages pass their own code, and /es/features/interview-warmup reports as
 * `es_features_interview-warmup`.
 */
export function candidatePracticeUrl(pathname: string, lang?: string): string {
  const page =
    cleanPath(pathname)
      .replace(/^\/+/, "")
      .toLowerCase()
      .replace(/[^a-z0-9_.-]+/g, "_")
      .slice(0, 64) || "home";
  return `${CANDIDATE_PRACTICE_URL}&utm_content=${page}${lang ? `&lang=${encodeURIComponent(lang)}` : ""}`;
}

const CANDIDATE_CTA_ROUTES = new Set([
  "/candidates",
  "/features/interview-warmup",
  "/features/ai-mock-interview",
  "/features/ai-video-interview",
]);

// Which blog posts are written for candidates lives in a plain JS module, so
// the practice-callout generator script can use the same rule.
export { isCandidateBlogPost, isGeneralInterestPost } from "./candidateBlogPosts.js";
import { isCandidateBlogPost } from "./candidateBlogPosts.js";

function cleanPath(pathname: string): string {
  // A trailing slash is the same route; anything else must not match.
  return (pathname || "").replace(/\/+$/, "");
}

function blogSlug(pathname: string): string | null {
  const m = cleanPath(pathname).match(/^\/blog\/([a-z0-9-]+)$/);
  return m ? m[1] : null;
}

/** Static routes only. Blog posts need the post data: use useCandidateCta. */
export function isCandidateCtaRoute(pathname: string): boolean {
  // The native-language versions of the practice pages (/es/features/interview-warmup).
  return CANDIDATE_CTA_ROUTES.has(cleanPath(pathname)) || parseNativeCandidatePath(pathname) !== null;
}

/**
 * True when the current marketing route should carry the candidate CTAs.
 * The blog data module is large and lazy-loaded with the blog routes, so the
 * shell imports it on demand (same chunk the article page already loads)
 * instead of bundling it into every marketing page.
 */
export function useCandidateCta(pathname: string): boolean {
  const slug = blogSlug(pathname);
  const [blogCandidate, setBlogCandidate] = useState(false);
  useEffect(() => {
    let live = true;
    if (!slug) {
      setBlogCandidate(false);
      return;
    }
    import("@/lib/blogPosts").then((m) => {
      if (live) setBlogCandidate(isCandidateBlogPost(m.getPost(slug)));
    });
    return () => {
      live = false;
    };
  }, [slug]);
  return isCandidateCtaRoute(pathname) || (slug !== null && blogCandidate);
}
